<template>
  <CardLayout
    v-model="editing"
    title="Smart Unit Information"
    header-type="edit"
    hint="Edit Smart Unit"
    pms-aware-edit
    :loading="loading"
  >
    <template #load>
      <em>Loading unit details</em>
    </template>
    <template #edit>
      <AlertsAnimated
        :alerts="localAlerts"
        dense
        @cancel-edit="cancelEdit"
        @proceed-edit="proceedEdit"
      />
      <ValidationObserver v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(saveUnit)">
          <v-row>
            <v-col>
              <PossiblePMS v-slot="{ disabled }">
                <ValidatedTextField
                  ref="smartUnitName"
                  v-model="subject.name"
                  name="Smart Unit #"
                  rules="required|max:250|min:2"
                  :disabled="disabled || !allowEditName"
                />
              </PossiblePMS>
            </v-col>
            <v-col>
              <PossiblePMS v-slot="{ disabled }">
                <SelectUnitType
                  v-model="subject.unitTypeId"
                  :disabled="disabled"
                />
              </PossiblePMS>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-textarea
                v-model="subject.description"
                label="Description"
                rows="2"
                class="unit-description"
              />
            </v-col>
            <v-col>
              <v-select
                v-model="subject.amenities"
                multiple
                :items="amenityOptions"
                label="Unit Amenities"
              />
            </v-col>
          </v-row>
          <v-row v-if="isUnitLevelEnabled">
            <v-col>
              <div class="text-body-2 mb-2">Unit Level</div>
              <v-radio-group
                v-model="subject.muteAlertsDuringDay"
                row
                class="mt-0"
              >
                <v-radio label="Standard Alert" :value="false" />
                <v-radio label="Pro Alert - After Hours" :value="true" />
              </v-radio-group>
            </v-col>
          </v-row>
          <PossiblePMS v-slot="{ disabled }">
            <v-btn small color="success" type="submit" :disabled="disabled">
              Save Smart Unit
            </v-btn>
          </PossiblePMS>
        </form>
      </ValidationObserver>
    </template>

    <dl class="property-list">
      <dt>Smart Unit #</dt>
      <dd>
        <span class="d-flex align-center">
          {{ smartUnit.name }}
          <div v-if="isActiveDemo" class="d-flex align-center">
            <v-icon right small color="orange">fas fa-user-clock</v-icon>

            <v-chip x-small color="orange" text-color="white" class="ml-1">
              Demo
            </v-chip>
          </div>
          <v-chip
            v-if="shouldShowProLabel(smartUnit)"
            x-small
            color="#1565c0"
            text-color="white"
            class="ml-2"
          >
            <v-icon left small color="#ffd700">mdi-crown</v-icon> PRO
          </v-chip>
        </span>
      </dd>
      <dt>Unit Type</dt>
      <dd>{{ displayUnitType(smartUnit.unitType) }}</dd>

      <dt v-if="smartUnit.description">Description</dt>
      <dd v-if="smartUnit.description" class="unit-description">
        {{ smartUnit.description }}
      </dd>
      <dt v-if="smartUnit.amenities && smartUnit.amenities.length > 0">
        Unit Amenities
      </dt>
      <dd v-if="smartUnit.amenities && smartUnit.amenities.length > 0">
        {{ smartUnit.amenities.map(titleCase).join(", ") }}
      </dd>

      <template
        v-if="
          isAutoEnrollment &&
          smartUnit.enrollmentInfo &&
          smartUnit.enrollmentInfo.moveInAt
        "
      >
        <dt>Move-In Date</dt>
        <dd>
          <div class="d-flex align-center mb-1">
            <v-icon x-small left color="primary">mdi-calendar</v-icon>
            <strong>{{ formatDate(smartUnit.enrollmentInfo.moveInAt) }}</strong>
          </div>
          <v-chip
            v-if="awaitingDays !== null"
            x-small
            :color="awaitingDaysChipColor"
            :text-color="awaitingDaysTextColor"
            outlined
          >
            {{ awaitingDaysLabel }}
          </v-chip>
        </dd>
        <template v-if="enrollmentStatusLabel">
          <dt>Enrollment Status</dt>
          <dd>
            <v-chip
              x-small
              :color="enrollmentStatusChipColor"
              :text-color="enrollmentStatusTextColor"
              outlined
            >
              <v-icon left x-small>mdi-circle-small</v-icon>
              {{ enrollmentStatusLabel }}
            </v-chip>
          </dd>
        </template>
      </template>

      <dt v-if="isUnitLevelEnabled">Unit Level</dt>
      <dd v-if="isUnitLevelEnabled">
        <v-radio-group
          v-model="unitLevelValue"
          row
          class="mt-0"
          @change="onUnitLevelChange"
        >
          <v-radio label="Standard Alert" :value="false" />
          <v-radio label="Pro Alert - After Hours" :value="true" />

          <v-btn
            v-if="unitLevelChanged"
            small
            :loading="unitLevelSaving"
            :disabled="unitLevelSaving"
            color="primary"
            class="mt-2"
            @click="saveUnitLevel"
          >
            Save
          </v-btn>
        </v-radio-group>
      </dd>
    </dl>
  </CardLayout>
</template>

<script>
import CardLayout from "@layout/CardLayout.vue";
import ValidatedTextField from "../utils/ValidatedTextField.vue";
import SelectUnitType from "../common/SelectUnitType.vue";
import PossiblePMS from "../common/PossiblePMS.vue";
import AlertsAnimated from "@tod-ui/components/AlertsAnimated.vue";
import { ValidationObserver } from "vee-validate";

import { ref, computed } from "vue";
import { titleCase } from "@tod-ui/helpers/strings";
import useSmartUnit from "./useSmartUnit";
import useAlerts from "@tod-ui/composables/useAlerts";
import useFacility from "../authentication/useFacility";
import usePMS from "../common/usePMS";
import { displayUnitType } from "@tod-ui/helpers/unitType";
import { UPDATE_SMART_ZONE } from "../smart_zones/graphql";
import { formatDate } from "@tod-ui/helpers/datetime";
import {
  ENROLLMENT_STATUS,
  awaitingDaysChipColor,
  awaitingDaysTextColor,
  daysWithoutDeviceLabel,
  enrollmentStatusChipColor,
  enrollmentStatusLabel,
  enrollmentStatusTextColor,
} from "./helper";

export default {
  name: "CardSmartUnitInfo",
  components: {
    ValidationObserver,
    ValidatedTextField,
    CardLayout,
    PossiblePMS,
    SelectUnitType,
    AlertsAnimated,
  },
  props: {
    value: {
      type: String,
      default: "",
    },
  },
  setup() {
    const {
      smartUnit,
      smartUnitLoading,
      unitAmenityOptions,
      updateSmartUnit,
      fetchSmartUnit,
      isActiveDemo,
    } = useSmartUnit();
    const {
      addLocalAlert,
      clearAllLocalAlerts,
      localAlerts,
      addAlert,
      clearAllAlerts,
    } = useAlerts();
    const { currentFacility } = useFacility();
    const { isAutoEnrollment } = usePMS();

    const subject = ref({});
    const allowEditName = ref(false);
    const unitLevelValue = ref(false);
    const originalUnitLevelValue = ref(false);
    const unitLevelChanged = ref(false);
    const unitLevelSaving = ref(false);

    const amenityOptions = unitAmenityOptions.map((val) => ({
      value: val,
      text: titleCase(val),
    }));
    const unitLevelAlertingOptions = [
      { value: false, text: "Standard Alert" },
      { value: true, text: "Pro Alert - After Hours" },
    ];

    const isUnitLevelEnabled = computed(() => {
      try {
        if (!currentFacility || !currentFacility.value) {
          return false;
        }
        const facility = currentFacility.value;
        if (!facility || !facility.profile) {
          return false;
        }
        const facilityProfile = facility.profile;
        return (
          facilityProfile?.muteAlertsDuring === "day" &&
          (facilityProfile?.muteAlertsOrganizationType ?? "facility") === "zone"
        );
      } catch (error) {
        console.warn("isUnitLevelEnabled error:", error);
        return false;
      }
    });

    return {
      smartUnit,
      loading: smartUnitLoading,
      isActiveDemo,
      unitLevelSaving,
      amenityOptions,
      unitLevelAlertingOptions,
      updateSmartUnit,
      fetchSmartUnit,
      addLocalAlert,
      clearAllLocalAlerts,
      localAlerts,
      addAlert,
      clearAllAlerts,
      subject,
      allowEditName,
      unitLevelValue,
      originalUnitLevelValue,
      unitLevelChanged,
      currentFacility,
      isUnitLevelEnabled,
      isAutoEnrollment,
      formatDate,
    };
  },
  computed: {
    editing: {
      get() {
        return this.value;
      },
      set(editingUnit) {
        this.$emit("input", editingUnit);
      },
    },
    awaitingDays() {
      const info = this.smartUnit?.enrollmentInfo;
      if (info?.status !== ENROLLMENT_STATUS.AWAITING_DEVICE) return null;
      return info.daysWithoutDevice ?? null;
    },
    awaitingDaysLabel() {
      return daysWithoutDeviceLabel(this.awaitingDays);
    },
    awaitingDaysChipColor() {
      return awaitingDaysChipColor(this.awaitingDays);
    },
    awaitingDaysTextColor() {
      return awaitingDaysTextColor(this.awaitingDays);
    },
    enrollmentStatusLabel() {
      return enrollmentStatusLabel(this.smartUnit?.enrollmentInfo?.status);
    },
    enrollmentStatusChipColor() {
      return enrollmentStatusChipColor(this.smartUnit?.enrollmentInfo?.status);
    },
    enrollmentStatusTextColor() {
      return enrollmentStatusTextColor(this.smartUnit?.enrollmentInfo?.status);
    },
  },
  watch: {
    smartUnit: {
      handler(newVal) {
        if (newVal?.profile) {
          const newValue = newVal.profile.muteAlertsDuringDay === true;
          this.unitLevelValue = newValue;
          this.originalUnitLevelValue = newValue;
          this.unitLevelChanged = false;
        }
      },
      immediate: true,
      deep: true,
    },
    editing(editingUnit) {
      if (editingUnit) {
        this.clearAllLocalAlerts();
        try {
          this.subject = {
            name: this.smartUnit?.name || "",
            unitTypeId: this.smartUnit?.unitType?.id || null,
            description: this.smartUnit?.description || "",
            amenities: this.smartUnit?.amenities || [],
            muteAlertsDuringDay:
              this.smartUnit?.profile?.muteAlertsDuringDay === true,
          };
        } catch (error) {
          console.warn("Error initializing subject in editing watcher:", error);
          this.subject = {
            name: this.smartUnit?.name || "",
            unitTypeId: this.smartUnit?.unitType?.id || null,
            description: this.smartUnit?.description || "",
            amenities: this.smartUnit?.amenities || [],
            muteAlertsDuringDay: false,
          };
        }

        this.addLocalAlert({
          type: "warning",
          message:
            "Please, edit Smart Unit name only to fix a typo. Use Renters page to transfer a renter to a different Smart Unit.",
          actions: { "cancel-edit": "Cancel", "proceed-edit": "Fix Typo" },
        });
      } else {
        if (!this.subject.success) {
          this.clearAllAlerts();
        }
        this.subject = {};
        this.allowEditName = false;
      }
    },
  },
  methods: {
    proceedEdit() {
      this.clearAllLocalAlerts();
      this.allowEditName = true;
      this.$nextTick(() =>
        this.$refs.smartUnitName.$el.querySelector("input").focus()
      );
    },
    cancelEdit() {
      this.clearAllLocalAlerts();
    },
    onUnitLevelChange() {
      this.unitLevelChanged =
        this.unitLevelValue !== this.originalUnitLevelValue;
    },
    shouldShowProLabel(item) {
      try {
        if (!item || !item.profile) {
          return false;
        }
        const facility = this.currentFacility?.value || this.currentFacility;
        if (!facility || !facility.profile) {
          return false;
        }
        const facilityProfile = facility.profile;
        const isUnitLevelEnabled =
          facilityProfile?.muteAlertsDuring === "day" &&
          (facilityProfile?.muteAlertsOrganizationType ?? "facility") ===
            "zone";

        return isUnitLevelEnabled && item.profile?.muteAlertsDuringDay === true;
      } catch (error) {
        console.warn("shouldShowProLabel error:", error);
        return false;
      }
    },
    async saveUnitLevel() {
      this.unitLevelSaving = true;
      this.clearAllAlerts();
      try {
        if (
          !this.smartUnit?.id ||
          !this.smartUnit?.name ||
          !this.smartUnit?.timeZone
        ) {
          throw "Missing required unit information";
        }

        await this.$apollo.mutate({
          mutation: UPDATE_SMART_ZONE,
          variables: {
            id: this.smartUnit.id,
            name: this.smartUnit.name,
            timeZone: this.smartUnit.timeZone,
            subtype: "unit",
            profile: {
              muteAlertsDuringDay: this.unitLevelValue,
            },
          },
        });

        await this.fetchSmartUnit(this.$apollo, this.smartUnit.id);

        this.originalUnitLevelValue = this.unitLevelValue;
        this.unitLevelChanged = false;
        this.addAlert({
          type: "success",
          message: "Unit level updated successfully",
          timeout: 15,
        });
      } catch (error) {
        const errorMessage =
          error?.message ||
          error ||
          "There was an error updating the Smart Unit";
        this.addAlert({
          type: "error",
          message: errorMessage,
        });
        this.unitLevelValue = this.originalUnitLevelValue;
        this.unitLevelChanged = false;
      } finally {
        this.unitLevelSaving = false;
      }
    },
    async saveUnit() {
      const isNameChanged =
        this.subject.name.toLowerCase() !== this.smartUnit.name.toLowerCase();

      this.clearAllAlerts();
      try {
        const message = await this.updateSmartUnit(
          this.$apollo,
          {
            name: this.subject.name,
            unitTypeId: this.subject.unitTypeId,
            description: this.subject.description,
            amenities: this.subject.amenities || [],
            muteAlertsDuringDay: this.subject.muteAlertsDuringDay,
          },
          isNameChanged
        );

        this.editing = "";
        this.subject.success = true;
        this.addAlert({
          type: "success",
          message,
          timeout: 15, // clears after 15 seconds
        });
      } catch (errorMessage) {
        this.addAlert({
          type: "error",
          message: errorMessage,
        });
      }
    },
    displayUnitType,
    titleCase,
  },
};
</script>

<style scoped>
.unit-description {
  color: gray;
  font-size: 0.9em;
}
</style>
